import { css } from 'aphrodite';
import {
	parseAsArrayOf,
	parseAsBoolean,
	parseAsIsoDate,
	parseAsString,
	parseAsStringEnum,
	useQueryState,
	useQueryStates,
} from 'nuqs';
import * as React from 'react';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { DealStageOptions, DemoStatusFilter } from '../../../extViewmodels';
import { AIConversationType } from '../../../extViewmodels/sdk/models/AI';
import { getDisplayName } from '../../../models/NameUtils';
import { useLeadDemosQuery } from '../../../queries/Lead/useLeadDemos';
import { useLeadDemosToConfirmQuery } from '../../../queries/Lead/useLeadDemosToConfirmQuery';
import { AiButton } from '../../../web/components-v2/AiButton';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { Dialog } from '../../../web/components-v2/Dialog';
import {
	AppDrawerContent,
	AppDrawerToolbarButton,
	CoffeeDrawerProvider,
	CoffeeDrawerToolbar,
	SideDrawer,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { Databoard, DataboardBody } from '../../../web/components/Databoard';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../web/components/DataGrid';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { bs } from '../../../web/styles/styles';
import { DemoRecordingsModal, IDemoRecording } from '../../components/DemoRecordingsModal';
import { useSingleDemoAnalysis } from '../../components/DemoRecordingsModal/useSingleDemoAnalysis';
import { OutcomeInfo } from '../../components/leadView/components/OutcomeInfo';
import { TextColumn } from '../../components/leadView/components/TextColumn';
import { DemoDateWithRecording } from '../../components/leadView/LeadViewRowElement/components/DemoDateWithRecording';
import { styleSheet } from '../../components/leadView/LeadViewRowElement/styles';
import { destructive, gold, green } from '../../styles/colors';
import { FieldKey } from '../../viewModels/form';
import { ILeadSearchView } from '../../viewModels/leads/interfaces';
import { LeadServedSource } from '../../viewModels/queue';
import { BulkActionsBar } from './Analysis/components/BulkActionsBar';
import { DemoAnalysisDrawerContent } from './Analysis/components/DemoAnalysisDrawerContent';
import { DEMO_ANALYSIS_DRAWER_VALUE } from './Analysis/constants';
import { useMultiDemoSelectionAnalysis } from './Analysis/hooks/useMultiDemoSelectionAnalysis';
import { Filters } from './Filters';
import { CoachingTemplateSelector } from '../Settings/CallAnalysis/components/CoachingTemplateSelector';
import { styleSheet as demosStyleSheet } from './styles';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { buildDemoFilterBody } from './utils';

// Get attempts display text
const getAttemptsText = (searchView: ILeadSearchView) => {
	const confirmationAttempts = searchView.nextMeeting?.confirmationAttempts || 0;
	if (confirmationAttempts === 0) return 'No Attempts';
	return `${confirmationAttempts} Attempt${confirmationAttempts > 1 ? 's' : ''}`;
};

// Get attempts color based on confirmation status and attempt count
const getAttemptsColor = (searchView: ILeadSearchView) => {
	// Determine if demo is confirmed - check if the outcome name is "Demo Confirmed"
	const isDemoConfirmed = searchView.lastInteraction?.outcome?.name === 'Demo Confirmed';
	if (isDemoConfirmed) {
		return green; // Green for confirmed demos
	}
	const confirmationAttempts = searchView.nextMeeting?.confirmationAttempts || 0;
	// For unconfirmed demos, use traffic light system
	if (confirmationAttempts <= 1) {
		return destructive; // Red for 0-1 attempts
	} else if (confirmationAttempts === 2) {
		return gold; // Yellow for 2 attempts
	} else {
		return green; // Green for 3+ attempts
	}
};

// Deal stages that should NOT show the recording link (lowercase for case-insensitive comparison)
const EXCLUDED_RECORDING_STAGES = ['appointment no show', 'appointment rescheduled', 'appointment scheduled'] as const;

// Check if a deal stage should exclude the recording link
const isExcludedRecordingStage = (stage?: string) => {
	if (!stage) return false;
	return EXCLUDED_RECORDING_STAGES.some(excluded => stage.toLowerCase() === excluded.toLowerCase());
};

export const DemosContainer: React.FC = () => {
	const id = React.useId();
	const [userId] = useQueryState('userId', parseAsString);
	const [dateRange] = useQueryStates({
		from: parseAsIsoDate,
		to: parseAsIsoDate,
	});
	const [demoStatus] = useQueryState(
		'demoStatus',
		parseAsStringEnum<DemoStatusFilter>(Object.values(DemoStatusFilter)).withDefault(DemoStatusFilter.DemosToConfirm)
	);
	const [outcomeName] = useQueryState('outcomeName', parseAsStringEnum<FieldKey>(Object.values(FieldKey)));
	const [managerUserId] = useQueryState('managerUserId', parseAsString);
	const [demoPerformerUserId] = useQueryState('demoPerformerUserId', parseAsArrayOf(parseAsString));
	const [companyId] = useQueryState('companyId', parseAsString);

	// Past Demos tab filter state (multi-select arrays)
	const [dealStatus] = useQueryState('dealStatus', parseAsArrayOf(parseAsString));
	const [leadSource] = useQueryState('leadSource', parseAsArrayOf(parseAsString));
	const [addOn] = useQueryState('addOn', parseAsArrayOf(parseAsString));
	const [inboundChannel] = useQueryState('inboundChannel', parseAsArrayOf(parseAsString));
	const [lostReasonCategories] = useQueryState('lostReasonCategories', parseAsArrayOf(parseAsString));
	const [industry] = useQueryState('industry', parseAsArrayOf(parseAsString));
	const [tag] = useQueryState('tag', parseAsArrayOf(parseAsString));
	const [isColdDemo] = useQueryState('isColdDemo', parseAsBoolean);
	const [overbook] = useQueryState('overbook', parseAsBoolean);
	const [followUpDateRange] = useQueryStates({
		followUpDateStart: parseAsIsoDate,
		followUpDateEnd: parseAsIsoDate,
	});
	const [targetCloseDateRange] = useQueryStates({
		targetCloseDateStart: parseAsIsoDate,
		targetCloseDateEnd: parseAsIsoDate,
	});

	// State for demo selection
	const [selectedDemos, setSelectedDemos] = useState<Set<string>>(new Set());
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

	// Drawer state for demo analysis
	const [selectedDrawerValue, setSelectedDrawerValue] = React.useState<string | null>(null);
	const [analysisSource, setAnalysisSource] = React.useState<'single' | 'multi' | null>(null);

	// Build the POST filter body from all filter params (includes dealStage computation)
	const filterBody = React.useMemo(() => {
		let dealStage: string[] = [];
		switch (demoStatus) {
			case DemoStatusFilter.DemosToReschedule:
				dealStage = [DealStageOptions.AppointmentNoShow];
				break;
			case DemoStatusFilter.UpcomingDemos:
				dealStage = [DealStageOptions.AppointmentScheduled, DealStageOptions.AppointmentRescheduled];
				break;
			case DemoStatusFilter.PastDemos:
				if (dealStatus && dealStatus.length > 0) {
					dealStage = dealStatus;
				}
				break;
			default:
				break;
		}
		return buildDemoFilterBody({
			userId,
			managerUserId,
			companyId,
			outcomeName: outcomeName !== FieldKey.Unknown ? outcomeName : undefined,
			demoPerformerUserId,
			dealStage,
			start: dateRange.from ?? undefined,
			end: dateRange.to ?? undefined,
			leadSource,
			addOn,
			inboundChannel,
			lostReasonCategories,
			industry,
			tag,
			isColdDemo,
			overbook,
			followUpDateStart: followUpDateRange.followUpDateStart ?? undefined,
			followUpDateEnd: followUpDateRange.followUpDateEnd ?? undefined,
			targetCloseDateStart: targetCloseDateRange.targetCloseDateStart ?? undefined,
			targetCloseDateEnd: targetCloseDateRange.targetCloseDateEnd ?? undefined,
		});
	}, [
		userId,
		managerUserId,
		companyId,
		outcomeName,
		demoStatus,
		dealStatus,
		demoPerformerUserId,
		dateRange.from,
		dateRange.to,
		leadSource,
		addOn,
		inboundChannel,
		lostReasonCategories,
		industry,
		tag,
		isColdDemo,
		overbook,
		followUpDateRange.followUpDateStart,
		followUpDateRange.followUpDateEnd,
		targetCloseDateRange.targetCloseDateStart,
		targetCloseDateRange.targetCloseDateEnd,
	]);

	const isPastDemosTab = demoStatus === DemoStatusFilter.PastDemos;
	const hasPastDemosDateRange = !!(dateRange.from && dateRange.to);

	// Determine which endpoint to use: demosToConfirm (default upcoming) vs demos (filtered/past/companyId)
	const hasDealStageFilter =
		demoStatus === DemoStatusFilter.DemosToReschedule ||
		demoStatus === DemoStatusFilter.UpcomingDemos ||
		(demoStatus === DemoStatusFilter.PastDemos && (dealStatus?.length ?? 0) > 0);
	const dataSource =
		!hasDealStageFilter && demoStatus !== DemoStatusFilter.PastDemos && !companyId ? 'demosToConfirm' : 'demos';
	const leadDemosToConfirmQuery = useLeadDemosToConfirmQuery({
		enabled: dataSource === 'demosToConfirm',
		filterBody,
		meta: {
			toastErrorMessage: 'An unexpected error occurred while loading demos.',
			errorLogging: {
				category: 'DemosContainer',
				action: 'useLeadDemosToConfirmQuery',
			},
		},
	});
	const leadDemosQuery = useLeadDemosQuery({
		// Past Demos: disabled by default, fetched explicitly via refetch() on Search click
		enabled: dataSource === 'demos' && !isPastDemosTab,
		filterBody,
		meta: {
			toastErrorMessage: 'An unexpected error occurred while loading demos.',
			errorLogging: {
				category: 'DemosContainer',
				action: 'useLeadDemosQuery',
			},
		},
	});
	const activeQuery = dataSource === 'demosToConfirm' ? leadDemosToConfirmQuery : leadDemosQuery;
	const isLoading = activeQuery.isFetching;
	const isSuccess = activeQuery.isSuccess;
	const isEmpty = activeQuery.isSuccess && activeQuery.data.pages[0].values.length === 0;

	// Clear selection when any filter changes (including "clear all filters")
	// Serialize all filter values to a stable string to detect changes
	const filterKey = React.useMemo(
		() =>
			JSON.stringify([
				userId,
				demoStatus,
				outcomeName,
				managerUserId,
				demoPerformerUserId,
				companyId,
				dealStatus,
				leadSource,
				addOn,
				inboundChannel,
				lostReasonCategories,
				industry,
				tag,
				isColdDemo,
				overbook,
				dateRange.from?.toISOString(),
				dateRange.to?.toISOString(),
				followUpDateRange.followUpDateStart?.toISOString(),
				followUpDateRange.followUpDateEnd?.toISOString(),
				targetCloseDateRange.targetCloseDateStart?.toISOString(),
				targetCloseDateRange.targetCloseDateEnd?.toISOString(),
			]),
		[
			userId,
			demoStatus,
			outcomeName,
			managerUserId,
			demoPerformerUserId,
			companyId,
			dealStatus,
			leadSource,
			addOn,
			inboundChannel,
			lostReasonCategories,
			industry,
			tag,
			isColdDemo,
			overbook,
			dateRange.from,
			dateRange.to,
			followUpDateRange.followUpDateStart,
			followUpDateRange.followUpDateEnd,
			targetCloseDateRange.targetCloseDateStart,
			targetCloseDateRange.targetCloseDateEnd,
		]
	);
	// Search button handler: reset the infinite query back to page 1 (so we don't
	// re-fetch every previously loaded page), then refetch. refetch() ignores the
	// `enabled` flag and always fires.
	const handleSearch = React.useCallback(async () => {
		setSelectedDemos(new Set());
		await reactQueryClient.resetQueries({ queryKey: ['lead/demos/filter'] });
		activeQuery.refetch();
	}, [activeQuery]);

	const prevFilterKeyRef = React.useRef(filterKey);
	React.useEffect(() => {
		if (prevFilterKeyRef.current !== filterKey) {
			setSelectedDemos(new Set());
			prevFilterKeyRef.current = filterKey;
		}
	}, [filterKey]);

	const [recordingsModal, setRecordingsModal] = useState<{
		demoId?: string;
		demoDate?: string;
		companyName?: string;
	}>({});

	// Single demo analysis hook for analyzing individual recordings from the modal
	const {
		startAnalysis: startSingleAnalysis,
		closeAnalysis: closeSingleAnalysis,
		isLoading: isSingleAnalysisLoading,
		isError: isSingleAnalysisError,
		isCreatingConversation: isSingleAnalysisCreatingConversation,
		sendFollowUp: sendSingleAnalysisFollowUp,
		conversationId: singleAnalysisConversationId,
		aiPromptMutation: singleAnalysisAiPromptMutation,
		analysisPhase: singleAnalysisPhase,
	} = useSingleDemoAnalysis();

	const [analyzingRecordingId, setAnalyzingRecordingId] = useState<string | null>(null);

	const handleRecordingClick = (demoId: string, demoDate: string, _closerOwnerId: string, companyName?: string) => {
		setRecordingsModal({
			demoId,
			demoDate,
			companyName,
		});
	};

	const handleCloseModal = () => {
		setRecordingsModal({ demoId: undefined, demoDate: undefined, companyName: undefined });
	};

	const handleStartSingleAnalysis = (recording: IDemoRecording, templateId?: string) => {
		setAnalyzingRecordingId(recording.uuid);
		startSingleAnalysis(recording, templateId);
		handleCloseModal();
		setAnalysisSource('single');
		setSelectedDrawerValue(DEMO_ANALYSIS_DRAWER_VALUE);
	};

	const handleCloseSingleAnalysis = () => {
		setAnalyzingRecordingId(null);
		closeSingleAnalysis();
		setAnalysisSource(null);
		setSelectedDrawerValue(null);
	};

	// Multi-demo selection analysis hook
	const {
		startMultiAnalysis,
		closeAnalysis: closeMultiAnalysis,
		isLoading: isMultiAnalysisLoading,
		isLoadingTranscripts: isMultiAnalysisLoadingTranscripts,
		isCreatingConversation: isMultiAnalysisCreatingConversation,
		sendFollowUp: sendMultiAnalysisFollowUp,
		conversationId: multiAnalysisConversationId,
		aiPromptMutation: multiAnalysisAiPromptMutation,
		// New hierarchical analysis state
		analysisPhase: multiAnalysisPhase,
		currentDemoIndex: multiCurrentDemoIndex,
		totalDemos: multiTotalDemos,
		individualAnalyses: multiIndividualAnalyses,
	} = useMultiDemoSelectionAnalysis({
		selectedLeadSearchViews: activeQuery.data
			? activeQuery.data.pages.flatMap(page => page.values).filter(item => selectedDemos.has(item.company?.id || ''))
			: [],
		source: 'demos_page_selection',
		agentTemplateId: selectedTemplateId || undefined,
	});

	const isSingleSource = analysisSource === 'single';
	const isMultiSource = analysisSource === 'multi';

	// Computed drawer value - only open if we have analysis context
	const hasMultiAnalysisResults = multiAnalysisPhase !== 'idle' || multiIndividualAnalyses.length > 0;
	const hasSingleAnalysisResults = isSingleSource;
	const hasAnalysisResults = hasMultiAnalysisResults || hasSingleAnalysisResults;
	const drawerValue = hasAnalysisResults ? selectedDrawerValue : null;

	// Handle demo selection
	const handleDemoSelect = (demoId: string, checked: boolean) => {
		const newSelected = new Set(selectedDemos);
		if (checked) {
			newSelected.add(demoId);
		} else {
			newSelected.delete(demoId);
		}
		setSelectedDemos(newSelected);
	};

	// Check if a demo should show the recording link (has recording capability)
	const hasRecordingLink = (item: ILeadSearchView) => {
		const itemCompanyId = item.company?.id;
		const itemDealStage = item.lastDeal?.stage;
		return itemCompanyId && !isExcludedRecordingStage(itemDealStage);
	};

	// Handle select all demos with recordings
	const handleSelectAllWithRecordings = (checked: boolean) => {
		if (!isSuccess || !activeQuery.data) return;
		const data = activeQuery.data.pages.flatMap(page => page.values);
		const newSelected = new Set<string>();
		if (checked) {
			for (const item of data) {
				if (hasRecordingLink(item)) {
					newSelected.add(item.company!.id);
				}
			}
		}
		setSelectedDemos(newSelected);
	};

	// Compute select all checkbox state (memoized to avoid recalculating on every render)
	const selectAllState = React.useMemo(() => {
		if (!isSuccess || !activeQuery.data) return { checked: false, indeterminate: false };
		const data = activeQuery.data.pages.flatMap(page => page.values);
		const demosWithRecordingLink = data.filter(hasRecordingLink);
		if (demosWithRecordingLink.length === 0) return { checked: false, indeterminate: false };
		const selectedCount = demosWithRecordingLink.filter(item => selectedDemos.has(item.company!.id)).length;
		const allSelected = selectedCount === demosWithRecordingLink.length;
		const someSelected = selectedCount > 0 && !allSelected;
		return { checked: allSelected, indeterminate: someSelected };
	}, [isSuccess, activeQuery.data, selectedDemos]);

	// Handle analyze selected demos - auto-open drawer
	const handleAnalyzeSelected = () => {
		if (selectedDemos.size === 0) return;
		startMultiAnalysis();
		setAnalysisSource('multi');
		setSelectedDrawerValue(DEMO_ANALYSIS_DRAWER_VALUE);
	};

	// Handle close multi-analysis
	const handleCloseMultiAnalysis = () => {
		closeMultiAnalysis();
		setAnalysisSource(null);
		setSelectedDrawerValue(null);
		setSelectedDemos(new Set());
	};

	// Close drawer if single analysis transcript fetch fails
	React.useEffect(() => {
		if (isSingleAnalysisError && isSingleSource) {
			setAnalyzingRecordingId(null);
			setAnalysisSource(null);
			setSelectedDrawerValue(null);
		}
	}, [isSingleAnalysisError, isSingleSource]);

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	const noop = React.useCallback(() => {}, []);

	// Past Demos: show prompt to search before loading any data
	if (isPastDemosTab && !isLoading && !isSuccess) {
		return (
			<CoffeeDrawerProvider value={null} onValueChange={noop}>
				<div className={css(demosStyleSheet.pageRoot)}>
					<div className={css(demosStyleSheet.pageContent)}>
						<DemosLayout onSearch={handleSearch} searchDisabled={!hasPastDemosDateRange}>
							<div className={css(bs.textCenter, bs.p2, bs.textGray600)}>
								{hasPastDemosDateRange ? (
									<>
										Set your filters and click <strong>Search</strong> to load demos.
									</>
								) : (
									'Select a date range to search past demos.'
								)}
							</div>
						</DemosLayout>
					</div>
				</div>
			</CoffeeDrawerProvider>
		);
	}

	if (isLoading) {
		const loadedCount = activeQuery.data?.pages.reduce((sum, page) => sum + page.values.length, 0) ?? 0;
		return (
			<CoffeeDrawerProvider value={null} onValueChange={noop}>
				<div className={css(demosStyleSheet.pageRoot)}>
					<div className={css(demosStyleSheet.pageContent)}>
						<DemosLayout onSearch={handleSearch} isSearching>
							<div className={css(bs.textCenter, bs.p2)}>
								<LoadingSpinner type='large' />
								<SearchingProgress loadedCount={loadedCount} />
							</div>
						</DemosLayout>
					</div>
				</div>
			</CoffeeDrawerProvider>
		);
	}
	if (isEmpty) {
		return (
			<CoffeeDrawerProvider value={null} onValueChange={noop}>
				<div className={css(demosStyleSheet.pageRoot)}>
					<div className={css(demosStyleSheet.pageContent)}>
						<DemosLayout onSearch={handleSearch} isSearching={activeQuery.isFetching}>
							<div className={css(bs.textCenter, bs.p2)}>No data to show</div>
						</DemosLayout>
					</div>
					<SideDrawer styles={[demosStyleSheet.drawer]}>
						<AppDrawerContent value={DEMO_ANALYSIS_DRAWER_VALUE} styles={[demosStyleSheet.drawerContent]}>
							<DemoAnalysisDrawerContent
								onClose={noop}
								isLoading={false}
								isCreatingConversation={false}
								onSendFollowUp={noop}
								conversationId=''
								aiPromptMutation={{ isLoading: false, isStreaming: false } as any}
							/>
						</AppDrawerContent>
						<CoffeeDrawerToolbar>
							<AppDrawerToolbarButton value={DEMO_ANALYSIS_DRAWER_VALUE} tooltip='Demo analysis' disabled={true}>
								<SparklesIcon />
							</AppDrawerToolbarButton>
						</CoffeeDrawerToolbar>
					</SideDrawer>
				</div>
			</CoffeeDrawerProvider>
		);
	}

	if (isSuccess) {
		const data = activeQuery.data.pages.flatMap(page => page.values);

		return (
			<CoffeeDrawerProvider value={drawerValue} onValueChange={setSelectedDrawerValue}>
				<div className={css(demosStyleSheet.pageRoot)}>
					<div className={css(demosStyleSheet.pageContent)}>
						<DemosLayout onSearch={handleSearch} isSearching={activeQuery.isFetching}>
							{isPastDemosTab && selectedDemos.size > 0 && (
								<BulkActionsBar selectedDemoCount={selectedDemos.size}>
									<CoachingTemplateSelector
										value={selectedTemplateId}
										onValueChange={setSelectedTemplateId}
										coachingTypes={[AIConversationType.SalesDemoCallCoaching]}
									/>
									<AiButton
										title='Analyze Selected'
										onClick={handleAnalyzeSelected}
										disabled={isMultiAnalysisLoadingTranscripts}
										isLoading={isMultiAnalysisLoadingTranscripts}
										size='sm'
									/>
								</BulkActionsBar>
							)}
							<Databoard>
								<DataboardBody styles={[demosStyleSheet.databoardBodyOverride]}>
									<DataGrid>
										<DataGridColumns>
											{isPastDemosTab && (
												<DataGridColumn>
													<div style={{ marginLeft: -10 }}>
														<Checkbox
															checked={selectAllState.indeterminate ? 'indeterminate' : selectAllState.checked}
															onCheckedChange={checked => handleSelectAllWithRecordings(checked === true)}
															aria-label='Select all demos with recordings'
														/>
													</div>
												</DataGridColumn>
											)}
											<DataGridColumn styles={[demosStyleSheet.demoPerformDateColumn]}>
												Demo perform date
											</DataGridColumn>
											<DataGridColumn>Company info</DataGridColumn>
											<DataGridColumn>SDR</DataGridColumn>
											<DataGridColumn>Closer</DataGridColumn>
											<DataGridColumn styles={[demosStyleSheet.lastCallColumn]}>Last call</DataGridColumn>
										</DataGridColumns>
										<DataGridRows>
											{data.map((item, index) => {
												const demoId = item.company?.id || '';
												const isSelected = selectedDemos.has(demoId);
												return (
													<DataGridRow key={`${id}-lead-search-row-${index}`}>
														{isPastDemosTab && (
															<DataGridRowItem>
																<Checkbox
																	checked={isSelected}
																	onCheckedChange={checked => handleDemoSelect(demoId, checked as boolean)}
																	aria-label={`Select demo for ${item.company?.companyName || 'Unknown'}`}
																/>
															</DataGridRowItem>
														)}
														<DataGridRowItem>
															<DemoDateWithRecording
																dateValue={item.nextMeeting?.startDate ?? item.lastDeal?.demoPerformDate}
																demoId={item.company?.id}
																closerOwnerId={item.lastDeal?.creator?.id}
																dealStage={item.lastDeal?.stage}
																companyName={item.company?.companyName}
																onRecordingClick={handleRecordingClick}
															/>
														</DataGridRowItem>
														<DataGridRowItem>
															{item.company?.id ? (
																<Button asChild variant='link' styles={[styleSheet.companyLink]}>
																	<NavLink
																		to={`/queue?from=${LeadServedSource.AppointmentConfirmation}&companyId=${item.company?.id}`}
																	>
																		<TextColumn
																			text={item.company?.companyName || 'Unknown'}
																			subtext={item.company?.phoneNumbers[0]?.metadata?.standard || 'Unknown'}
																		/>
																	</NavLink>
																</Button>
															) : (
																<TextColumn
																	text={item.company?.companyName || 'Unknown'}
																	subtext={item.company?.phoneNumbers[0]?.metadata?.standard || 'Unknown'}
																/>
															)}
														</DataGridRowItem>
														<DataGridRowItem>
															<TextColumn
																text={item.lastDeal?.creator ? getDisplayName(item.lastDeal.creator) : 'Unknown'}
																isSmall={true}
															/>
														</DataGridRowItem>
														<DataGridRowItem>
															<TextColumn
																text={item.lastDeal?.demoPerformer ? getDisplayName(item.lastDeal.demoPerformer) : '—'}
																isSmall={true}
															/>
														</DataGridRowItem>
														<DataGridRowItem>
															<div>
																<OutcomeInfo
																	name={item.lastInteraction?.outcome?.name}
																	color={item.lastInteraction?.outcome?.hexColor}
																	date={item.lastInteraction?.date}
																	meetingParticipants={item.nextMeeting?.participants}
																/>
																<div className={css(styleSheet.lastInteraction)}>
																	<div className={css(styleSheet.dot)} style={{ background: getAttemptsColor(item) }} />
																	<span>{getAttemptsText(item)}</span>
																</div>
															</div>
														</DataGridRowItem>
													</DataGridRow>
												);
											})}
										</DataGridRows>
									</DataGrid>
									{activeQuery.hasNextPage && !activeQuery.isFetchingNextPage ? (
										<Waypoint
											key='load-more-trigger'
											bottomOffset='-200px'
											onEnter={() => activeQuery.fetchNextPage()}
										/>
									) : null}
									{activeQuery.isFetchingNextPage ? (
										<div className={css(bs.flex, bs.justifyCenter, bs.p2)}>
											<LoadingSpinner type='small' />
										</div>
									) : null}
								</DataboardBody>
								<Dialog
									open={Boolean(recordingsModal.demoId)}
									onOpenChange={open => {
										if (!open) {
											handleCloseModal();
										}
									}}
								>
									<DemoRecordingsModal
										demoId={recordingsModal.demoId}
										demoDate={recordingsModal.demoDate}
										companyName={recordingsModal.companyName}
										onStartAnalysis={handleStartSingleAnalysis}
										analyzingRecordingId={analyzingRecordingId}
										isAnalyzeLoading={isSingleAnalysisLoading}
									/>
								</Dialog>
							</Databoard>
						</DemosLayout>
					</div>
					<SideDrawer styles={[demosStyleSheet.drawer]}>
						<AppDrawerContent value={DEMO_ANALYSIS_DRAWER_VALUE} styles={[demosStyleSheet.drawerContent]}>
							<DemoAnalysisDrawerContent
								onClose={isSingleSource ? handleCloseSingleAnalysis : handleCloseMultiAnalysis}
								isLoading={isSingleSource ? isSingleAnalysisLoading : isMultiAnalysisLoading}
								isCreatingConversation={
									isSingleSource ? isSingleAnalysisCreatingConversation : isMultiAnalysisCreatingConversation
								}
								onSendFollowUp={isSingleSource ? sendSingleAnalysisFollowUp : sendMultiAnalysisFollowUp}
								conversationId={isSingleSource ? singleAnalysisConversationId : multiAnalysisConversationId}
								aiPromptMutation={isSingleSource ? singleAnalysisAiPromptMutation : multiAnalysisAiPromptMutation}
								analysisPhase={isSingleSource ? singleAnalysisPhase : multiAnalysisPhase}
								currentDemoIndex={isMultiSource ? multiCurrentDemoIndex : undefined}
								totalDemos={isMultiSource ? multiTotalDemos : undefined}
								individualAnalyses={isMultiSource ? multiIndividualAnalyses : undefined}
							/>
						</AppDrawerContent>
						<CoffeeDrawerToolbar>
							<AppDrawerToolbarButton
								value={DEMO_ANALYSIS_DRAWER_VALUE}
								tooltip='Demo analysis'
								disabled={!hasAnalysisResults}
							>
								<SparklesIcon />
							</AppDrawerToolbarButton>
						</CoffeeDrawerToolbar>
					</SideDrawer>
				</div>
			</CoffeeDrawerProvider>
		);
	}
};

function DemosLayout({
	children,
	onSearch,
	isSearching,
	searchDisabled,
}: {
	children: React.ReactNode;
	onSearch: () => void;
	isSearching?: boolean;
	searchDisabled?: boolean;
}) {
	return (
		<div className={css(demosStyleSheet.demosLayoutContainer)}>
			<div className={css(demosStyleSheet.demosLayoutContainerInner)}>
				<div className={css(demosStyleSheet.demosLayoutContent)}>
					<div className={css(demosStyleSheet.demosLayoutHeader)}>
						<h2 className={css(demosStyleSheet.demosLayoutTitle)}>Demos</h2>
					</div>
					<Filters onSearch={onSearch} isSearching={isSearching} searchDisabled={searchDisabled} />
					{children}
				</div>
			</div>
		</div>
	);
}

function SearchingProgress({ loadedCount }: { loadedCount: number }) {
	const [elapsed, setElapsed] = React.useState(0);

	React.useEffect(() => {
		setElapsed(0);
		const interval = setInterval(() => setElapsed(s => s + 1), 1000);
		return () => clearInterval(interval);
	}, []);

	return (
		<p className={css(bs.textGray600, bs.textSm)} style={{ marginTop: 12 }}>
			{loadedCount > 0 ? `Loaded ${loadedCount} demos` : 'Searching'}
			{elapsed > 0 ? ` (${elapsed}s)` : ''}...
		</p>
	);
}
